import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GradingIcon from "@mui/icons-material/Grading";
import GroupIcon from "@mui/icons-material/Group";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import InfoIcon from "@mui/icons-material/Info";
import MessageIcon from "@mui/icons-material/Message";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import PageviewIcon from "@mui/icons-material/Pageview";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ThumbsUpDownIcon from "@mui/icons-material/ThumbsUpDown";
import WorkIcon from "@mui/icons-material/Work";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import * as Constants from "config";
import MessageNotification from "notifications/entities/message-notification/message-notification";
import NotificationsAPIService from "notifications/entities/notification/api/notifications-api-service";
import NotificationQuery, {
  NotificationFilterParameters,
  NotificationOrderField,
  NotificationOrderParameters,
} from "notifications/entities/notification/api/request-contracts/notification-query";
import { NotificationLink, NotificationType } from "notifications/entities/notification/notification-type";
import {
  addNotification,
  getNotificationsByQuery,
  isNotificationLoadIssue,
  markAllNotificationsAsSeen,
  NotificationLoadIssue,
  populateNotificationsByQuery,
} from "notifications/entities/notification/store/notifications-redux-slice";
import { Action } from "notifications/values/action/action";
import { BoxType } from "notifications/values/box-type/box-type";
import TopicCategory from "notifications/values/topic-category/topic-category";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import type Notification from "notifications/entities/notification/notification";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { useSelector } from "react-redux";

const BellIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  margin: theme.spacing(0.5),
}));
const NotificationMenu = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    backdropFilter: "blur(10px) brightness(105%)",
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  zIndex: theme.zIndex.modal + 1,
}));
const MarkAllReadButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.4),
  marginLeft: theme.spacing(1.6),
}));
const ViewAllButton = styled(Button)(() => ({
  borderRadius: 0,
  width: "100%",
}));
const NotificationList = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(40),
  overflow: "auto",
  padding: 0,
  width: theme.spacing(44),
}));
const NotificationItem = styled(ListItemButton, { shouldForwardProp: (prop) => prop !== "seen" })<{
  seen?: boolean;
}>(({ seen, theme }) => ({
  backgroundColor: seen ? 'rgba(243, 243, 243, 0.5)' : 'rgba(255, 255, 255, 0.5)',
  "&:first-of-type": {
    borderTop: "none",
  },
  "&:last-child": {
    borderBottom: "none",
  },
  "&:hover": {
    backgroundColor: seen ? "#f3f3f3" : theme.palette.common.white,
  },
}));
const TitleContainer = styled("div")(() => ({
  alignItems: "baseline",
  display: "flex",
  justifyContent: "space-between",
}));
const SentDateContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  alignItems: "center",
  columnGap: theme.spacing(0.5),
  textAlign: "right",
  minWidth: theme.spacing(8),
}));
const SentDate = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  textAlign: "right",
  whiteSpace: "nowrap",
}));

export default function NotificationBell() {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  const interfaceName = "notificationBell";
  const query = useMemo(() => {
    return new NotificationQuery({
      filterParameters: new NotificationFilterParameters({
        boxType: BoxType.Inbox,
        isUnread: true,
      }),
      orderParameters: new NotificationOrderParameters({
        orderBy: NotificationOrderField.CreatedDate,
        orderDir: OrderDirection.Descending,
      }),
      paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 25 }),
    });
  }, []);

  const notifications = useSelector(getNotificationsByQuery(query));

  const dispatch = useAttorneyHubDispatch();

  const numUnread =
    notifications?.data.filter(
      (notification: Notification | NotificationLoadIssue) =>
        "isSeenByUser" in notification && !notification.isSeenByUser(session.user?.id)
    ).length ?? 0;

  useEffect(() => {
    dispatch(populateNotificationsByQuery({ session, query, interfaceName }));
  }, [dispatch, query, session, session.user?.id?.value]);

  useEffect(() => {
    if (notifications === undefined) {
      dispatch(populateNotificationsByQuery({ session, query, interfaceName }));
    }
  }, [dispatch, notifications, query, session]);

  function handleBellClick(event: React.MouseEvent<HTMLButtonElement>) {
    setMenuAnchor(menuAnchor ? null : event.currentTarget);
  }

  async function handleNotificationClick(_event: React.MouseEvent<HTMLElement>, notification: NotificationType) {
    try {
      const seenNotification = await new NotificationsAPIService(session).markNotificationAsSeen(notification);
      if (!seenNotification) throw new Error("Notification not returned from API");
      dispatch(addNotification(seenNotification));

      if (!seenNotification.isSeenByUser) {
        console.error(`Failed to mark notification ${notification.id} as seen.`);
      }

      const isMessageNotification = notification.topic?.category?.startsWith("Messaging");
      if (notification.topic?.id && isMessageNotification) {
        const messageNotification = notification as MessageNotification;

        if (!messageNotification?.messageInfo) {
          enqueueSnackbar("Message information is currently unavailable. Please try again later.", { variant: "warning" });
          navigate(`/communications`);
          return;
        }

        const isProposalMessage = messageNotification?.messageInfo?.forumInfo.entityClass.value === "Work.Proposal";
        if (!isProposalMessage) {
          navigate(`/communications/forums/view/${(notification as MessageNotification)?.messageInfo?.forumInfo.id}`);
          return;
        }
      }

      const redirectUrl = NotificationLink(notification);
      if (!redirectUrl || redirectUrl === globalThis.location.pathname) return;
      navigate(redirectUrl);
    } catch (error) {
      console.error(error);
    } finally {
      setMenuAnchor(null);
    }
  }

  async function handleMarkAllClick() {
    try {
      if (numUnread < 1) return;
      await new NotificationsAPIService(session).markAllNotificationsAsSeen();
      dispatch(markAllNotificationsAsSeen(session));
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't mark all notifications as read. Please try again.", { variant: "error" });
    }
  }

  function handleViewAllClick() {
    setMenuAnchor(null);
    if (location.pathname.startsWith("/communications")) return;
    navigate("/communications");
  }

  function getNotificationTitle(notification: NotificationType) {
    let title: string;
    const sentDate = notification.createdDate?.format("MMM D");
    const sentTime = notification.createdDate?.format("h:mma");
    if (notification.topic?.category === TopicCategory.Messaging.Message) {
      title = `Message from ${notification.messageInfo?.senderName?.value ?? "Unknown Sender"}`;
    } else {
      title =
        Constants.notificationTypes[(notification.topic?.category ?? "") as keyof typeof Constants.notificationTypes] ??
        "Notification";
    }
    return (
      <TitleContainer>
        {title}
        {sentDate && (
          <SentDateContainer>
            <SentDate variant="caption">{sentDate}</SentDate>
            <SentDate variant="caption">{sentTime}</SentDate>
          </SentDateContainer>
        )}
      </TitleContainer>
    );
  }

  return (
    <React.Fragment>
      <BellIconButton aria-label="open notifications menu" onClick={handleBellClick} size="large">
        <Badge overlap="rectangular" badgeContent={numUnread > 0 ? numUnread : null} color="error">
          <NotificationsIcon />
        </Badge>
      </BellIconButton>
      <NotificationMenu
        anchorEl={menuAnchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MarkAllReadButton
          color="primary"
          startIcon={<PlaylistAddCheckIcon />}
          disabled={numUnread < 1}
          onClick={handleMarkAllClick}
        >
          Mark All As Read
        </MarkAllReadButton>
        <Divider />
        <NotificationList>
          {(!notifications || notifications.data.length < 1) && (
            <NotificationItem seen={true} disabled>
              <ListItemIcon>
                <NotificationsOffOutlinedIcon />
              </ListItemIcon>
              <ListItemText>No Unread Notifications</ListItemText>
            </NotificationItem>
          )}
          {notifications?.data.map((notification: NotificationType | NotificationLoadIssue) =>
            isNotificationLoadIssue(notification) ? (
              <Typography key={notification.id.value} color="error" sx={{ padding: 2 }}>
                Issue loading this notification.
              </Typography>
            ) : (
              <NotificationItem
                key={notification.id.value}
                seen={Boolean(notification.isSeenByUser(session.user?.id))}
                divider
                onClick={(event) => handleNotificationClick(event, notification)}
              >
                <ListItemIcon>
                  {[TopicCategory.Messaging.Message, TopicCategory.Messaging.Message].includes(
                    notification.topic?.category ?? ""
                  ) && <MessageIcon />}
                  {notification.topic?.category === TopicCategory.Users.NetworkInvitation && <PersonAddIcon />}
                  {notification.topic?.category === TopicCategory.Users.NetworkConnection && <AccountTreeIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal &&
                    notification.actionRequired === Action.AcceptOrReject && <ThumbsUpDownIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal &&
                    notification.actionRequired === Action.Review && <PageviewIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal &&
                    notification.actionRequired === Action.DraftProposalReview && <PageviewIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal &&
                    notification.actionRequired === Action.DraftProposalReviewApproved && <GradingIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal &&
                    notification.actionRequired === Action.ProposalReview && <PageviewIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal &&
                    notification.actionRequired === Action.ProposalReviewApproved && <GradingIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal &&
                    notification.actionRequired === Action.ReviewOrReject && <PageviewIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal &&
                    notification.actionRequired === Action.ProposalRevisionReview && <AssignmentIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal &&
                    notification.actionRequired === Action.HireOrCancel && <WorkIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal &&
                    notification.actionRequired === Action.Hired && <AssignmentIcon />}
                  {notification.topic?.category === TopicCategory.Work.Proposal && !notification.actionRequired && (
                    <InfoIcon />
                  )}
                  {notification.topic?.category === TopicCategory.Work.Agreement && <WorkIcon />}
                  {notification.topic?.category === TopicCategory.Marketplace.Team && <GroupIcon />}
                  {notification.topic?.category === TopicCategory.LegalEntities.EntityMember && <PersonIcon />}
                  {notification.topic?.category === TopicCategory.Marketplace.TeamInvitation && <GroupAddIcon />}
                  {notification.topic?.category === TopicCategory.LegalEntities.LegalEntity && (
                    <SupervisorAccountIcon />
                  )}
                </ListItemIcon>
                <ListItemText primary={getNotificationTitle(notification)} secondary={notification.message?.value} />
              </NotificationItem>
            )
          )}
        </NotificationList>
        <Divider />
        <ViewAllButton color="primary" onClick={handleViewAllClick}>
          View All
        </ViewAllButton>
      </NotificationMenu>
    </React.Fragment>
  );
}
